"use client";

import { Browser } from "@capacitor/browser";
import { useEffect, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useParticipantRuntimePolicy } from "@/hooks/useParticipantRuntimePolicy";
import { useMediaAuth } from "@/hooks/useMediaAuth";
import { resolveAbsoluteApiUrl } from "@/lib/api";
import {
  resolveAppleMusicErrorState,
  signInWithAppleMusic,
  type AppleMusicErrorTone,
} from "@/lib/appleMusicAuth";
import { warmAppleMusicKit } from "@/lib/appleMusicKit";
import { reportProviderClientIssue } from "@/lib/providerClientIssue";
import { isNativePlatform } from "@/lib/runtime";
import {
  buildSpotifyLoginPath,
  clearSpotifyForceReauth,
  consumeSpotifyForceReauth,
} from "@/lib/spotifyAuth";
import {
  normalizeLinkAccountId,
  normalizeReturnTo,
} from "@/lib/authNavigation";
import { useStore } from "@/store";

const LOGIN_HERO_MESSAGES = [
  "🎵 Sync your music with emotions.",
  "📊 Track GSR responses in real time.",
  "💡 Turn reactions into research data.",
];

export function useLoginController() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const setCurrentStep = useStore.use.setCurrentStep();
  const setRequestedReturnTo = useStore.use.setRequestedReturnTo();

  const {
    isAuthed,
    isChecking,
    authStatus,
    error,
    user,
    checkSession,
  } = useMediaAuth();

  const [messageIndex, setMessageIndex] = useState(0);
  const [appleLoginLoading, setAppleLoginLoading] = useState(false);
  const [appleLoginError, setAppleLoginError] = useState<string | null>(null);
  const [appleLoginErrorTone, setAppleLoginErrorTone] =
    useState<AppleMusicErrorTone>("error");
  const appleMusicKitPrimedRef = useRef(false);

  const requestedReturnTo = normalizeReturnTo(
    searchParams?.get("returnTo") ?? null,
    "/presurvey"
  );
  const linkAccountId = normalizeLinkAccountId(
    searchParams?.get("linkAccountId") ?? null
  );
  const providerHint = searchParams?.get("provider") ?? null;
  const isAppleLinkFlow = providerHint === "applemusic" && Boolean(linkAccountId);
  const azureError = searchParams?.get("azure_error")
    ? "WPI sign-in failed. Please try again."
    : null;
  const participantRuntimePolicy = useParticipantRuntimePolicy();
  const participantGateTriggered = participantRuntimePolicy
    ? !participantRuntimePolicy.participantLoginAllowed
    : false;
  const participantGateReady = participantRuntimePolicy !== null;
  const participantGateMessage =
    participantRuntimePolicy?.message ??
    "Checking participant browser support...";

  useEffect(() => {
    setCurrentStep("login");
  }, [setCurrentStep]);

  useEffect(() => {
    setRequestedReturnTo(requestedReturnTo);
  }, [requestedReturnTo, setRequestedReturnTo]);

  useEffect(() => {
    document.documentElement.setAttribute("data-scrollbar-gutter-mode", "auto");
    return () => {
      document.documentElement.removeAttribute("data-scrollbar-gutter-mode");
    };
  }, []);

  useEffect(() => {
    if (isAuthed && !isAppleLinkFlow) {
      clearSpotifyForceReauth();
      if (user?.is_admin) {
        router.replace("/admin");
        return;
      }
      if (!participantGateReady || participantGateTriggered) {
        return;
      }
      router.replace(requestedReturnTo);
    }
  }, [
    isAppleLinkFlow,
    isAuthed,
    participantGateReady,
    participantGateTriggered,
    requestedReturnTo,
    router,
    user?.is_admin,
  ]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setMessageIndex((current) => (current + 1) % LOGIN_HERO_MESSAGES.length);
    }, 2500);
    return () => {
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (appleMusicKitPrimedRef.current) return;
    appleMusicKitPrimedRef.current = true;
    void warmAppleMusicKit().catch((err) => {
      console.warn("[Login] Apple Music warmup failed", err);
    });
  }, []);

  const primeAppleMusicKit = () => {
    if (appleMusicKitPrimedRef.current) return;
    appleMusicKitPrimedRef.current = true;
    void warmAppleMusicKit().catch(() => {
      // Ignore warmup failures; button click retries with explicit error.
    });
  };

  const handleSpotifyLogin = async () => {
    const shouldForceReauth = Boolean(consumeSpotifyForceReauth());
    if (isNativePlatform()) {
      const loginPath = buildSpotifyLoginPath({
        returnTo: requestedReturnTo,
        native: true,
        force: shouldForceReauth,
      });
      const loginUrl = resolveAbsoluteApiUrl(loginPath);
      try {
        await Browser.open({ url: loginUrl });
      } catch (providerError) {
        reportProviderClientIssue({
          provider: "spotify",
          category: "auth",
          level: "error",
          event: "login_launch_failed",
          title: "Spotify sign-in failed to open",
          message: "Spotify sign-in could not open in the native browser.",
          error: providerError,
          details: {
            nativePlatform: true,
            returnTo: requestedReturnTo,
            shouldForceReauth,
          },
        });
      }
      return;
    }

    window.location.href = buildSpotifyLoginPath({
      returnTo: requestedReturnTo,
      force: shouldForceReauth,
    });
  };

  const handleYouTubeLogin = async () => {
    if (isNativePlatform()) {
      const loginPath = `/api/youtube/login?native=1&returnTo=${encodeURIComponent(requestedReturnTo)}`;
      const loginUrl = resolveAbsoluteApiUrl(loginPath);
      try {
        await Browser.open({ url: loginUrl });
      } catch (providerError) {
        reportProviderClientIssue({
          provider: "youtube",
          category: "auth",
          level: "error",
          event: "login_launch_failed",
          title: "YouTube sign-in failed to open",
          message: "YouTube sign-in could not open in the native browser.",
          error: providerError,
          details: {
            nativePlatform: true,
            returnTo: requestedReturnTo,
          },
        });
      }
      return;
    }

    window.location.href = `/api/youtube/login?returnTo=${encodeURIComponent(
      requestedReturnTo
    )}`;
  };

  const handleAppleMusicLogin = async () => {
    setAppleLoginError(null);
    setAppleLoginErrorTone("error");
    setAppleLoginLoading(true);

    try {
      const payload = await signInWithAppleMusic({
        returnTo: requestedReturnTo,
        linkAccountId,
      });
      await checkSession();

      const currentUser = useStore.getState().user;
      if (!currentUser?.user_id) {
        const sessionError = new Error(
          "Apple Music sign-in did not establish a session"
        );
        reportProviderClientIssue({
          provider: "applemusic",
          category: "auth",
          level: "error",
          event: "login_session_missing",
          title: "Apple Music sign-in did not create a session",
          message: sessionError.message,
          error: sessionError,
          details: {
            returnTo: requestedReturnTo,
            linkAccountId,
            payloadReturnTo:
              typeof payload?.returnTo === "string" ? payload.returnTo : null,
          },
        });
        throw sessionError;
      }

      const nextPath =
        typeof payload?.returnTo === "string" && payload.returnTo.startsWith("/")
          ? payload.returnTo
          : requestedReturnTo;
      router.replace(currentUser.is_admin ? "/admin" : nextPath);
    } catch (loginError) {
      const resolved = resolveAppleMusicErrorState(loginError);
      setAppleLoginError(resolved.message);
      setAppleLoginErrorTone(resolved.tone);
      setAppleLoginLoading(false);
    }
  };

  const checkingSession = isChecking || appleLoginLoading;
  const participantLoginDisabled =
    checkingSession || !participantGateReady || participantGateTriggered;
  const participantDisabledHint = participantLoginDisabled
    ? !participantGateReady
      ? "Checking participant browser support..."
      : participantGateMessage
    : null;

  return {
    appleLoginError,
    appleLoginErrorTone,
    authError: authStatus === "unauth" ? error : null,
    azureError,
    checkingSession,
    handleAppleMusicLogin,
    handleOpenResearchLogin: () => {
      router.push(`/research-login?returnTo=${encodeURIComponent("/admin")}`);
    },
    handleSpotifyLogin,
    handleYouTubeLogin,
    heroMessage: LOGIN_HERO_MESSAGES[messageIndex],
    isAppleLinkFlow,
    participantDisabledHint,
    participantGateMessage,
    participantGateTriggered,
    participantLoginDisabled,
    primeAppleMusicKit,
    showMobileBuilds: !isNativePlatform(),
  };
}
